import React, { useState, useEffect, useRef } from 'react';
import { Box, Text, useColorModeValue, useToken, Flex } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { apiGetSalesPerformance } from 'api';
import type { ViewAsState } from 'api';
import { DynamicContestWidget } from '../../components/SalesContest/DynamicContestWidget';
import Card from 'components/card/Card';

interface Props {
  viewAs?: ViewAsState;
}

export const SalesContestDashboard = ({ viewAs }: Props) => {
  const bgColor = useColorModeValue('white', 'navy.700') as string;
  const titleColor = useColorModeValue('navy.700', 'white');
  const [brandColor] = useToken('colors', ['brand.400']);
  const [activeBranchId, setActiveBranchId] = useState<number | null>(null);
  const hasInitialized = useRef(false);

  const { data, isLoading } = useQuery({
    queryKey: ['sales-performance', viewAs ?? { type: 'self' }],
    queryFn: () => apiGetSalesPerformance(viewAs),
    refetchInterval: 300000,
    refetchOnWindowFocus: true,
  }) as {
    data: any;
    isLoading: boolean;
  };

  useEffect(() => {
    if (data && !hasInitialized.current && data.branch_list?.length > 0) {
      setActiveBranchId(data.branch_list[0].id);
      hasInitialized.current = true;
    }
  }, [data]);

  useEffect(() => {
    if (data && hasInitialized.current && data.branch_list?.length > 0) {
      const branchExists = data.branch_list.some(
        (b: any) => b.id === activeBranchId,
      );
      if (!branchExists) {
        setActiveBranchId(data.branch_list[0].id);
      }
    }
  }, [data, activeBranchId]);

  const rows: any[] = [];

  if (data) {
    if (data.big_performance) {
      rows.push({
        label: data.big_performance.label,
        percentage: data.big_performance.pace || 0,
        color: '#f97316',
      });
    }
    if (data.broker_performance) {
      rows.push({
        label: data.broker_performance.label,
        percentage: data.broker_performance.pace || 0,
        color: '#2c5282',
      });
    }

    const branches = data.branch_list || [];

    if (branches.length > 0) {
      const currentBranch =
        branches.find((b: any) => b.id === activeBranchId) || branches[0];

      rows.push({
        label: currentBranch.label,
        percentage: currentBranch.pace || 0,
        color: brandColor as string,
        options: branches,
        onSelect: (id: number) => {
          setActiveBranchId(id);
        },
      });
    }
  }

  // If no data to display (all null/empty), return empty
  if (!isLoading && data && rows.length === 0) {
    return null;
  }

  return (
    <Card bg={bgColor} p="15px" borderRadius="16px">
      <Flex justify="space-between" align="center" mb={2}>
        <Text
          fontSize={{ base: '17px', hxl: '18px' }}
          fontWeight="700"
          color={titleColor}
        >
          BIG Shift into Gear Sales Incentive
        </Text>
      </Flex>
      <Box w="full" p={0} m={0}>
        {isLoading || !data ? (
          <Box p={10} textAlign="center">
            <Text
              fontSize="lg"
              fontWeight="500"
              color="black"
              className="animate-pulse"
            >
              Loading Contest Data...
            </Text>
          </Box>
        ) : (
          <DynamicContestWidget rows={rows} />
        )}
      </Box>
    </Card>
  );
};
